/**
 * ExtractionSuggestionControls
 *
 * Displays a compact UI for showing an extraction suggestion alongside
 * controls to accept the suggestion and inspect its source. The component
 * early-returns when suggestions are hidden or empty, normalizes any
 * provided `sourcePages` to numeric page indices, and uses local state
 * (`anchorEl`) to toggle a MUI `Popover` that exposes the `sourceUrl` and
 * page references. Implementation uses Material UI primitives (`Stack`,
 * `Typography`, `Button`, `IconButton`, `Popover`, `Link`) and keeps the
 * layout minimal so it can be embedded inline with form controls.
 *
 * It is used by Outcome, Important Dates and Offering Terms fields to show extraction suggestions for those fields.
 */
import React from 'react'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Link from '@mui/material/Link'
import Popover from '@mui/material/Popover'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { useSuggestionDatapointsVisibility } from './PressReleasesExtractionsContext'

type ExtractionSuggestionControlsProps = {
    suggestionText: string | null
    onUse: () => void
    disableUse?: boolean
    sourceUrl?: string | null
    sourcePages?: Array<string | number> | null
    quote?: string | null
}

function ExtractionSuggestionControls({
    suggestionText,
    onUse,
    disableUse = false,
    sourceUrl,
    sourcePages,
    quote,
}: ExtractionSuggestionControlsProps) {
    const { showSuggestionDatapoints } = useSuggestionDatapointsVisibility()
    const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null)
    if (!showSuggestionDatapoints || !suggestionText) return null

    const normalizedSourcePages = (sourcePages ?? [])
        .map((page) => (typeof page === 'number' ? page : Number(page)))
        .filter(Number.isFinite)

    const popoverOpen = Boolean(anchorEl)

    const handleSourceClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget)
    }

    const handleSourceClose = () => {
        setAnchorEl(null)
    }

    return (
        <>
            <Stack direction='row' alignItems='center' spacing={0} sx={{ ml: 0.5 }}>
                <Typography variant='body2' color='text.secondary' sx={{ whiteSpace: 'nowrap', fontWeight: 400 }}>
                    {suggestionText}
                </Typography>
                {sourceUrl && (
                    <IconButton
                        size='small'
                        onClick={handleSourceClick}
                        color={popoverOpen ? 'info' : 'default'}
                        aria-label='Show source details'
                        aria-haspopup='dialog'
                        aria-expanded={popoverOpen ? 'true' : undefined}
                    >
                        <InfoOutlinedIcon fontSize='small' />
                    </IconButton>
                )}
                <Button size='small' onClick={onUse} disabled={disableUse} sx={{ textTransform: 'none' }}>
                    Använd
                </Button>
            </Stack>
            {sourceUrl && (
                <Popover
                    open={popoverOpen}
                    anchorEl={anchorEl}
                    onClose={handleSourceClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                >
                    <Stack sx={{ p: 1.5, maxWidth: 420 }}>
                        {quote && (
                            <Typography variant='body2' color='text.primary' sx={{ mb: 0.75, fontStyle: 'italic' }}>
                                "{quote}"
                            </Typography>
                        )}
                        <Link
                            href={sourceUrl}
                            target='_blank'
                            rel='noopener noreferrer'
                            color='info.main'
                            sx={{ textDecoration: 'none', wordBreak: 'break-all' }}
                        >
                            {sourceUrl}
                        </Link>
                        {normalizedSourcePages.length > 0 ? (
                            <Typography variant='caption' color='text.secondary' sx={{ mt: 0.75 }}>
                                {normalizedSourcePages.length === 1
                                    ? `p. ${normalizedSourcePages[0]}`
                                    : `pp. ${normalizedSourcePages.join(', ')}`}
                            </Typography>
                        ) : null}
                    </Stack>
                </Popover>
            )}
        </>
    )
}

export default ExtractionSuggestionControls
